import { BlobReader, BlobWriter, configure, ZipReader, type FileEntry } from '@zip.js/zip.js'
import {
  compareFileNames, IMAGE_HEADER_LIMIT, parseImageHeader, UnknownDimensionsError,
  type Dimensions, type PageSource,
} from '@tanko/shared'
import { imageMediaType } from '../lib/fs-access'

type DimensionLookup = (page: number) => Promise<Dimensions | null | undefined>

// 不另外開 worker：解壓走原生 DecompressionStream，本來就不阻塞主執行緒，
// 而索引本身已經在 Web Worker 內，再巢狀開 worker 只是多一層零件。
configure({ useWebWorkers: false })

export class EncryptedArchiveError extends Error {
  constructor() {
    super('這是加密的壓縮檔，本應用不支援。請先解開密碼再匯入。')
    this.name = 'EncryptedArchiveError'
  }
}

export class UnreadableArchiveError extends Error {
  constructor(cause?: unknown) {
    super('無法讀取這個壓縮檔，它可能已損毀或不是 ZIP 格式。', { cause })
    this.name = 'UnreadableArchiveError'
  }
}

/** 壓縮檔內的圖片項目，依完整項目路徑自然排序。 */
async function imageEntries(reader: ZipReader<unknown>): Promise<FileEntry[]> {
  let entries
  try {
    entries = await reader.getEntries()
  } catch (cause) {
    throw new UnreadableArchiveError(cause)
  }
  const images = entries.filter(
    (entry): entry is FileEntry => !entry.directory && !!imageMediaType(entry.filename),
  )
  // 加密只擋圖片項目；壓縮檔裡若有加密的說明文字檔不影響閱讀。
  if (images.some(entry => entry.encrypted)) throw new EncryptedArchiveError()
  return images.sort((a, b) => compareFileNames(a.filename, b.filename))
}

/**
 * 以壓縮檔為來源的 PageSource。只讀中央目錄取得項目清單，
 * 取單頁時才解壓那一個項目，不把整個壓縮檔載進記憶體。
 */
export class ArchiveSource implements PageSource {
  private disposed = false

  private constructor(
    private readonly reader: ZipReader<unknown>,
    private readonly entries: readonly FileEntry[],
    private readonly lookup?: DimensionLookup,
  ) {}

  static async create(file: Blob, lookup?: DimensionLookup): Promise<ArchiveSource> {
    const reader = new ZipReader(new BlobReader(file))
    try {
      return new ArchiveSource(reader, await imageEntries(reader), lookup)
    } catch (cause) {
      await reader.close().catch(() => undefined)
      throw cause
    }
  }

  /** 壓縮檔內的圖片項目路徑，依頁序排列。上傳的來源快照需要它。 */
  get entryNames(): string[] {
    return this.entries.map(entry => entry.filename)
  }

  get pageCount(): number {
    return this.entries.length
  }

  private entryAt(page: number): FileEntry {
    if (this.disposed) throw new Error('壓縮檔來源已釋放')
    if (!Number.isInteger(page) || page < 0 || page >= this.entries.length) {
      throw new RangeError('頁碼超出範圍')
    }
    return this.entries[page]
  }

  async getPage(page: number): Promise<Blob> {
    const entry = this.entryAt(page)
    const type = imageMediaType(entry.filename) ?? 'application/octet-stream'
    try {
      return await entry.getData(new BlobWriter(type))
    } catch (cause) {
      if (entry.encrypted) throw new EncryptedArchiveError()
      throw new UnreadableArchiveError(cause)
    }
  }

  async getDimensions(page: number): Promise<Dimensions> {
    this.entryAt(page)
    const known = await this.lookup?.(page)
    if (known) return known
    const header = await (await this.getPage(page)).slice(0, IMAGE_HEADER_LIMIT).arrayBuffer()
    const parsed = parseImageHeader(header)
    if (!parsed) throw new UnknownDimensionsError(page)
    return parsed
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    await this.reader.close().catch(() => undefined)
  }
}

/** 掃描候選時用：數出壓縮檔裡有幾張圖，順便讓加密與損毀在這一步就被擋下。 */
export async function countArchivePages(file: Blob): Promise<number> {
  const source = await ArchiveSource.create(file)
  try {
    return source.pageCount
  } finally {
    await source.dispose()
  }
}
